import { useEffect, useMemo, useState } from 'react';
import {
  ResearchCard,
  MessageTabs,
  MessageTabData,
  GTMEvent,
  Message,
  MessageSkill,
  ArtifactType,
  TaskState,
} from 'src/types';
import { Tabs, TabPanel } from 'src/v2/commonComponents/Tabs';
import { prepareExternalModelsTabContent, sendGTMEvent } from 'src/utils';
import { renderMessageTabList } from '../MessageTabList';
import styles from './ThreadResearchCard.module.scss';

// TODO(olha): needs refactoring
import { ThreadMessageContent } from 'src/components/FlatAppearance/components/ThreadMessageContent';
import { ProgressBar } from 'src/components/FlatAppearance/components/ThreadCombinedCard/components/ProgressBar';
import { ThreadResearchReferences } from 'src/components/FlatAppearance/components/ThreadResearchCard/components/ThreadResearchReferences';
import { ThreadResearchMedia } from 'src/components/FlatAppearance/components/ThreadResearchCard/components/ThreadResearchMedia';
import { useMessageTab, useGetUser, useUserMetadata } from 'src/hooks';
import { FeedbackRating } from 'src/v2/components/ThreadMessage/components/FeedbackRating';
import {
  researcherModelsContainsThinking,
  NINJA_AGENT_TITLE,
  SUPER_AGENT_DEEP_RESEARCH_MODEL,
} from 'src/constants';
import { IconName } from 'src/components/Icon';
import { UpsellCard } from 'src/v2/commonComponents/UpsellCard';
import researcherDarkIcon from 'src/images/icons/researcher-doc-dark.svg';
import researcherLightIcon from 'src/images/icons/researcher-doc-light.svg';
import { NotifyMeButton } from 'src/components/NotifyMeButton';
import { DownloadFileTool } from 'src/v2/components/Editor/components/DownloadFileTool';
import { MoveToEditorTool } from '../MoveToEditorTool';
import { CopyMessageButton } from '../CopyMessageButton';
import { ShareButton } from 'src/components/ShareButton';
import { MoreMessageActionsTool } from '../MoreMessageActionsTool';
import { RelatedQuestions } from 'src/components/RelatedQuestions';
import { DownloadPodcastButton } from '../DownloadPodcastButton';
import { BoostWithSNButton } from '../BoostWithSNButton';

interface ThreadResearchCardProps {
  message: Message;
  messageId: string;
  researchCard?: ResearchCard | null;
  isStreaming?: boolean;
  messageSkill: MessageSkill | null;
  taskTitle: string;
  taskState?: TaskState;
  onSetActiveTab: (activeTab: string) => void;
}

// TODO(olha): Tabs feature needs deep refactoring
export const ThreadResearchCard = ({
  message,
  messageId,
  researchCard,
  isStreaming = false,
  messageSkill,
  taskTitle,
  taskState,
  onSetActiveTab,
}: ThreadResearchCardProps) => {
  const { isProTier, isStandardTier } = useGetUser();
  const { metadata } = useUserMetadata();

  const { model_ids } = message;

  const { search_result, deep_research_result, generative_ai_result } =
    researchCard?.data || {};
  const { referenced_model_summary } = generative_ai_result || {};
  const { references: external_models_result } = referenced_model_summary || {};
  const { web_page_references, image_references, video_references } =
    search_result || {};

  const withSuperAgent = useMemo(
    () => !!deep_research_result,
    [deep_research_result],
  );

  const tabsData: MessageTabData[] = useMemo(
    () => [
      ...(search_result
        ? [
            {
              tabName: MessageTabs.NINJA_AGENT,
              iconName: 'ninjaOutlined' as IconName,
              tabTitle:
                !!model_ids && !!metadata
                  ? metadata[model_ids[0] || '']?.display_name || ''
                  : NINJA_AGENT_TITLE,
              content:
                search_result.content?.content || search_result.summary || '',
              references: web_page_references,
              reasoning: search_result.content?.reasoning || undefined,
            },
          ]
        : []),
      ...(withSuperAgent
        ? [
            {
              tabName: MessageTabs.SUPER_AGENT,
              iconName: 'ninjaPixProLogo' as IconName,
              tabTitle:
                !!model_ids && !!metadata
                  ? metadata[model_ids[0] || '']?.display_name || ''
                  : 'Ninja SuperAgent',
              content:
                deep_research_result?.content?.content ||
                deep_research_result?.summary ||
                '',
              references: deep_research_result?.web_page_references,
              reasoning: deep_research_result?.content?.reasoning || undefined,
            },
          ]
        : []),
      ...(external_models_result
        ? prepareExternalModelsTabContent({
            references: external_models_result,
            metadata,
          })
        : []),
    ],
    [
      search_result,
      web_page_references,
      deep_research_result,
      external_models_result,
      withSuperAgent,
      model_ids,
      metadata,
    ],
  );

  const { getTabSRegistry, onTabClick } = useMessageTab({
    messageId,
    tabsData,
  });

  const [activeTab, setActiveTab] = useState<string>(
    tabsData[0]?.tabName || MessageTabs.NINJA_AGENT,
  );

  // TODO(olha): WIP Virtuoso
  useEffect(() => {
    if (tabsData[0]?.tabName) {
      setActiveTab(tabsData[0]?.tabName);
      onSetActiveTab(tabsData[0]?.tabName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsData[0]?.tabName]);

  const handleTabClick = (tabName: string) => {
    setActiveTab(tabName);
    onSetActiveTab(tabName);
    onTabClick(tabName);

    if (withSuperAgent && tabName === MessageTabs.SUPER_AGENT) {
      deep_research_result?.content?.content || deep_research_result?.summary
        ? sendGTMEvent(GTMEvent.SUPERAGENT_COMPLETE_CLICK)
        : sendGTMEvent(GTMEvent.SUPERAGENT_IN_PROGRESS_CLICK);
    }
  };

  const isDeepResearch = model_ids?.[0] === SUPER_AGENT_DEEP_RESEARCH_MODEL;

  const shouldDisableActions = useMemo(
    () => isStreaming || taskState !== TaskState.DONE,
    [isStreaming, taskState],
  );

  const withThinking = !!model_ids?.[0]
    ? researcherModelsContainsThinking.includes(model_ids[0])
    : false;

  return (
    <div className={styles.root}>
      <Tabs<string> defaultActiveTab={activeTab} onTabClick={handleTabClick}>
        {tabsData.length > 1 &&
          renderMessageTabList({
            data: tabsData,
            withSuperAgent,
            tabRegistry: getTabSRegistry(),
          })}

        {tabsData.map(
          ({ tabName, content, references, tabTitle, reasoning }) => (
            <TabPanel key={tabName} tab={tabName}>
              {content.length === 0 && !reasoning ? (
                <div className={styles.status}>
                  {tabName === MessageTabs.SUPER_AGENT &&
                    !!deep_research_result &&
                    isDeepResearch && (
                      <div className={styles.deepResearchProgress}>
                        <div className={styles.blinkingProgressText}>
                          Deep research digging up the internet for answers...
                        </div>

                        {<NotifyMeButton isThinking />}
                      </div>
                    )}

                  {(tabName !== MessageTabs.SUPER_AGENT ||
                    !!deep_research_result) &&
                    !isDeepResearch && (
                      <>
                        <div className={styles.blinkingProgressText}>
                          Generating the response...
                        </div>
                        {!withThinking && <ProgressBar count={3} withColor />}
                      </>
                    )}
                </div>
              ) : (
                <div className={styles.container}>
                  <ThreadMessageContent
                    content={content || ''}
                    isStreaming={isStreaming}
                    references={references}
                    reasoning={reasoning}
                    isDeepResearch={isDeepResearch}
                    messageSkill={messageSkill}
                  />

                  <ThreadResearchMedia
                    images={image_references || []}
                    videos={video_references || []}
                  />

                  {references && !isStreaming && (
                    <ThreadResearchReferences researchReferences={references} />
                  )}

                  {isDeepResearch && (isStandardTier || isProTier) && (
                    <div className={styles.upsellCardWrapper}>
                      <UpsellCard
                        darkIcon={researcherDarkIcon}
                        lightIcon={researcherLightIcon}
                        description="You have limited Deep Research tasks per day. Unlock unlimited access by upgrading to our Ultra or Business tier!"
                        dataGTM="Deep-Researcher-upsell-message"
                        shouldShowUpgrade
                      />
                    </div>
                  )}

                  {!isStreaming && (
                    <div className={styles.actionsWrapper}>
                      <BoostWithSNButton
                        message={message}
                        isDisabled={shouldDisableActions}
                      />

                      <DownloadPodcastButton
                        message={message}
                        activeTab={activeTab}
                        disabled={shouldDisableActions}
                      />

                      <DownloadFileTool
                        toolType="message"
                        message={message}
                        activeTab={activeTab}
                        disabled={shouldDisableActions}
                        taskTitle={taskTitle}
                      />

                      <MoveToEditorTool
                        message={message}
                        activeTab={activeTab}
                        componentType={ArtifactType.TEXT}
                        disabled={taskState !== TaskState.DONE}
                        taskTitle={taskTitle}
                      />

                      <CopyMessageButton
                        withTitle
                        withBackground
                        message={message}
                        activeTab={activeTab}
                        disabled={shouldDisableActions}
                        tooltipPlace="top"
                      />

                      <ShareButton
                        withTitle
                        messageId={messageId}
                        disabled={shouldDisableActions}
                        tooltipPlace="top"
                      />

                      <MoreMessageActionsTool
                        message={message}
                        disabled={shouldDisableActions}
                      />
                    </div>
                  )}

                  <RelatedQuestions
                    message={message}
                    isStreaming={isStreaming}
                  />

                  <FeedbackRating
                    tabTitle={tabTitle}
                    message={message}
                    isStreaming={isStreaming}
                    operationType="models"
                    modelId={tabName}
                    messageSkill={messageSkill}
                  />
                </div>
              )}
            </TabPanel>
          ),
        )}
      </Tabs>
    </div>
  );
};
